import React, { useState } from 'react';

import { Flex } from '@chakra-ui/react';
import styled from '@emotion/styled';
import Skeleton from '@material-ui/lab/Skeleton';
import { useToast } from 'components/hooks/useToast';
import { components } from 'react-select';
import AsyncSelect from 'react-select/async';
import { calculateDisplayEmail } from 'utils/helpers/netchexHelpers';

import { ArrowDown, Cross } from 'assets/assetsV1/iconComponents';

import { TOAST_STATUSES } from 'constants/ProjectConstants';

import { Avatar } from 'uiToolkitV1/Avatar';
import { TagColored } from 'uiToolkitV1/Tag';
import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

import { searchEmployeeForViolation } from '../hooks/addViolation';

const StyledTextLabel = styled(TextLabel)`
  overflow: hidden;
  white-space: ${(props) => (props.wrapText ? 'normal' : 'nowrap')};
  text-overflow: ellipsis;
  margin-left: 2px;
`;

const Option = (props) => {
  const { data } = props;
  const isDisabled = data?.isDisabled;
  const displayEmail = calculateDisplayEmail(data);
  return (
    <components.Option {...props} isDisabled={data?.isDisabled}>
      {isDisabled ? (
        <Flex alignItems="center" pb={1}>
          <Avatar name={data.label} src={data.imageUrl} />
          <Flex ml="12px" flexDirection="column">
            <Flex>
              <TextBodyMedium style={{ width: '150px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {data?.label}
              </TextBodyMedium>
              <TagColored color="grey">1:1 scheduled</TagColored>
            </Flex>
            <StyledTextLabel color="text.g2">{displayEmail}</StyledTextLabel>
          </Flex>
        </Flex>
      ) : (
        <Flex alignItems="center" pb={1}>
          <Avatar name={data.label} src={data.imageUrl} />
          <Flex ml="12px" flexDirection="column">
            <TextBodyMedium>{data.label}</TextBodyMedium>
            <StyledTextLabel color="text.g2">{displayEmail}</StyledTextLabel>
          </Flex>
        </Flex>
      )}
    </components.Option>
  );
};

const LoadingMessage = (props) => (
  <components.LoadingMessage {...props}>
    <>
      <div>
        <CircleSkeleton variant="circle" width={34} height={34} />
        <RectSkeleton variant="rect" width={180} height={22} />
      </div>
      <div>
        <CircleSkeleton variant="circle" width={34} height={34} />
        <RectSkeleton variant="rect" width={225} height={22} />
      </div>
    </>
  </components.LoadingMessage>
);

const SingleValue = ({ children, ...props }) => {
  const { data } = props;
  return (
    <components.SingleValue {...props}>
      <Flex alignItems="center" pb={1}>
        <Avatar name={data.label} src={data.imageUrl} />
        <TextBodyMedium ml={2}>{data.label}</TextBodyMedium>
      </Flex>
    </components.SingleValue>
  );
};

const ClearIndicator = (props) => {
  const {
    children,
    innerProps: { ref, ...restInnerProps },
  } = props;
  return (
    <div {...restInnerProps} ref={ref}>
      <Cross cursor="pointer" boxSize="18px" style={{ position: 'absolute', right: '-2px', top: '10px' }} />
    </div>
  );
};

const DropdownIndicator = (props) => {
  if (!props.hasValue) {
    return <ArrowDown cursor="pointer" boxSize="18px" style={{ position: 'absolute', right: '6px' }} />;
  }
  return null;
};

const SearchSelect = styled(AsyncSelect)`
  width: 100%;
  display: inline-block;

  .react-select-custom__group {
    .react-select-custom__option {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    &:first-child {
      .react-select-custom__group-heading {
        display: none;
      }
    }
    &:last-child {
      .react-select-custom__option {
        &:last-child {
          border: none;
        }
      }
    }
  }
  .react-select-custom__option--is-disabled {
    cursor: initial;
    opacity: 0.5;
    background: none;
    &:hover {
      background: none;
    }
  }
`;

const CircleSkeleton = styled(Skeleton)`
  display: inline-block;
`;

const RectSkeleton = styled(CircleSkeleton)`
  display: inline-block;
  vertical-align: super;
  margin-left: 1rem;
`;

const selectStyles = ({ employeeExists }) => ({
  control: (baseStyles) => ({
    ...baseStyles,
    padding: 0,
    border: 'none !important',
    boxShadow: 'none',
    outline: 'none',
  }),
  valueContainer: (baseStyles) => ({
    ...baseStyles,
    paddingLeft: 0,
    borderBottom: employeeExists ? '1px dashed #999999' : 'none',
  }),
  input: (baseStyles, state) => ({
    ...baseStyles,
    borderBottom: '1px dashed #999999',
    cursor: 'pointer',
    '&:hover': {
      'border-bottom-style': 'solid',
      borderColor: '#333333',
    },
    '&:focus-within': {
      'border-bottom-style': 'solid',
      borderColor: '#1565bc',
      boxShadow: '0px 2px 4px rgba(0, 142, 240, 0.1)',
    },
    '&:placeholder-shown': {
      'border-bottom-style': 'dashed',
    },
  }),
  option: (provided, { imageUrl }) => ({
    ...provided,
    cursor: 'pointer',
    padding: imageUrl ? '10px 14px' : '14px',
    fontWeight: 500,
    '&:hover': {
      background: 'rgba(230, 236, 240, 0.4)',
    },
  }),
  loadingMessage: (provided) => ({
    ...provided,
    padding: '1em 14px',
    textAlign: 'left',
  }),
  indicatorSeparator: () => ({ display: 'none' }),
});

const SearchEmployee = ({
  exclusions = [],
  onSelect,
  employeeMiniProfile,
  onClear,
  placeholder = 'Search the participant',
}) => {
  const [employee, setEmployee] = useState(employeeMiniProfile ?? null);
  const [searchText, setSearchText] = useState('');
  const toast = useToast();
  const employeeExists = !!employee;

  const NoOptionsMessage = (props) => (
    <components.NoOptionsMessage {...props}>
      {searchText ? 'No results found' : "Start typing the person's name"}
    </components.NoOptionsMessage>
  );

  const handleInputChange = (value) => setSearchText(value);

  const loadOptions = async (text) => {
    if (!text || !text.trim()) return [];
    try {
      const res = await searchEmployeeForViolation(text);
      const data = res.entity
        .map((searchedEmployee) => {
          const { id, displayName, profilePhoto } = searchedEmployee;
          return {
            ...searchedEmployee,
            displayName,
            filterTagType: 'EMPLOYEE_NAME',
            imageUrl: profilePhoto,
            uuid: id,
            value: id,
            label: displayName,
          };
        })
        ?.filter((item) => item !== null);

      return data;
    } catch (error) {
      console.error('Error fetching employees:', error);
      toast({
        title: 'Failed to fetch employees',
        status: TOAST_STATUSES.ERROR,
      });
      return [];
    }
  };

  const handleChange = (option) => {
    setEmployee(option);
    if (!option) {
      onClear();
    } else {
      onSelect(option);
    }
    setSearchText('');
  };

  return (
    <SearchSelect
      placeholder={placeholder}
      components={{
        LoadingMessage,
        NoOptionsMessage,
        Option,
        SingleValue,
        DropdownIndicator,
        ClearIndicator,
      }}
      loadOptions={loadOptions}
      onInputChange={handleInputChange}
      classNamePrefix="react-select-custom"
      onChange={handleChange}
      menuPlacement="auto"
      value={employee}
      styles={selectStyles({ employeeExists })}
      isClearable
      blurInputOnSelect
      isSearchable={!employeeExists}
      noOptionsMessage={
        employeeExists ? () => null : () => (searchText ? 'No results found' : "Start typing the person's name")
      }
    />
  );
};

export { SearchEmployee };
